import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { BarChart3, Trophy, TrendingUp, AlertCircle } from 'lucide-react';
import type { BenchmarkData } from '@/hooks/useDviBenchmarks';

interface DviBenchmarkComparisonProps {
  benchmarks: BenchmarkData | null | undefined;
  isLoading: boolean;
  shopOptedIn: boolean;
}

export function DviBenchmarkComparison({ benchmarks, isLoading, shopOptedIn }: DviBenchmarkComparisonProps) {
  if (!shopOptedIn) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Peer Benchmarking
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 p-4 bg-muted rounded-lg">
            <AlertCircle className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="font-medium">Network Opt-In Required</p>
              <p className="text-sm text-muted-foreground">
                Enable network learning in Settings to access peer benchmarks
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Peer Benchmarking</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  const shop = benchmarks?.shop_metrics;
  const peers = benchmarks?.peer_metrics;
  const percentile = benchmarks?.percentile_rank;
  const insights = benchmarks?.insights ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5" />
          Peer Benchmarking
        </CardTitle>
        <CardDescription>
          How your shop compares to {peers?.shop_count ?? 0}+ similar shops
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Percentile Rank */}
        {percentile !== null && (
          <div className="flex items-center gap-4 p-4 bg-primary/5 rounded-lg">
            <Trophy className="h-8 w-8 text-primary" />
            <div>
              <p className="text-2xl font-bold text-primary">{percentile}th Percentile</p>
              <p className="text-sm text-muted-foreground">
                Your shop ranks better than {percentile}% of peers
              </p>
            </div>
          </div>
        )}

        {/* Metrics Comparison */}
        <div className="space-y-4">
          <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
            Metrics Comparison
          </h4>
          
          <MetricComparisonRow
            label="Approval Rate"
            shopValue={shop?.approval_rate}
            peerValue={peers?.median_approval_rate}
            format="percent"
          />
          
          <MetricComparisonRow
            label="Overcall Rate"
            shopValue={shop?.overcall_rate}
            peerValue={peers?.median_overcall_rate}
            format="percent"
            lowerIsBetter
          />
          
          <MetricComparisonRow
            label="Quality Score"
            shopValue={shop?.quality_score}
            peerValue={peers?.median_quality_score}
            format="score"
          />
        </div>

        {/* Insights */}
        {insights.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
              Insights
            </h4>
            <div className="space-y-2">
              {insights.map((insight, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <TrendingUp className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span>{insight}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface MetricComparisonRowProps {
  label: string;
  shopValue: number | null | undefined;
  peerValue: number | null | undefined;
  format: 'percent' | 'score';
  lowerIsBetter?: boolean;
}

function MetricComparisonRow({ label, shopValue, peerValue, format, lowerIsBetter }: MetricComparisonRowProps) {
  const formatValue = (v: number | null | undefined) => {
    if (v === null || v === undefined) return 'N/A';
    return format === 'percent' ? `${Math.round(v * 100)}%` : Math.round(v).toString();
  };

  const shopNum = shopValue ?? 0;
  const peerNum = peerValue ?? 0;
  
  let comparison: 'better' | 'worse' | 'same' = 'same';
  if (shopNum !== peerNum) {
    const isBetter = lowerIsBetter ? shopNum < peerNum : shopNum > peerNum;
    comparison = isBetter ? 'better' : 'worse';
  }

  const comparisonColors = {
    better: 'text-green-600',
    worse: 'text-red-600',
    same: 'text-muted-foreground',
  };

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span>{label}</span>
        <div className="flex gap-3">
          <span className={`font-medium ${comparisonColors[comparison]}`}>
            You: {formatValue(shopValue)}
          </span>
          <span className="text-muted-foreground">
            Peers: {formatValue(peerValue)}
          </span>
        </div>
      </div>
      <div className="flex gap-1 h-2">
        <div className="flex-1 bg-primary/20 rounded-full overflow-hidden">
          <div 
            className="h-full bg-primary rounded-full"
            style={{ width: `${Math.min((shopNum / 1) * 100, 100)}%` }}
          />
        </div>
        <div className="flex-1 bg-muted rounded-full overflow-hidden">
          <div 
            className="h-full bg-muted-foreground/30 rounded-full"
            style={{ width: `${Math.min((peerNum / 1) * 100, 100)}%` }}
          />
        </div>
      </div>
    </div>
  );
}
